import React, { useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import Attachment from '~/app/common/Attachment';
import useTranslation from '~/app/hooks/useTranslation';
import { useStoreActions, useStoreState } from '~/store';
import { ATTACHMENT_MAX_FILE_SIZE, ATTACHMENT_MAX_FILES } from '~/workorder/helper';
import { Attachment as WorkOrderAttachmentType, WorkOrderStatuses } from '~/workorder/types';

export interface AttachmentProps {
    attachments: WorkOrderAttachmentType[];
    workOrderId?: number;
    status?: number;
}

export type WorkOrderAttachmentComponent = React.FC<AttachmentProps>;

const WorkOrderAttachment: WorkOrderAttachmentComponent = ({ attachments, workOrderId, status }) => {

    const netWorkStatus = useStoreState(state => state.app.offline.status);
    const attachmentSaving = useStoreState(state => state.workOrder.attachmentSaving);
    const allowedFileExtensions = useStoreState(state => state.machines.allowedWorkOrderFileExtensions);
    const { saveWorkOrderAttachment, deleteWorkOrderAttachment } = useStoreActions(state => state.workOrder);
    const { getWorkOrderDetail } = useStoreActions(state => state.workOrder);
    const { t } = useTranslation('workorder');

    const canEditAttachments = useMemo(() => {
        return (
            workOrderId != null  
            && status != null
            && status !== WorkOrderStatuses.APPROVED
            && !netWorkStatus
        );
    }, [workOrderId, status, netWorkStatus]);

    const handleAddAttachments = useCallback(async (files: File[]) => {
        try {
            const response = await saveWorkOrderAttachment({ workOrderId: workOrderId!, files });
            if (response.success !== false) {
                toast.success(t('detail.attachmentUploadSuccess'));
                await getWorkOrderDetail({ id: workOrderId!, showLoading: false });
            }
        } catch {
            toast.error(t('detail.attachmentUploadError'));
        }
    }, [saveWorkOrderAttachment, workOrderId, getWorkOrderDetail, t]);

    const handleDeleteAttachment = useCallback(async (attachment: WorkOrderAttachmentType) => {
        try {
            const response = await deleteWorkOrderAttachment({ id: attachment.archiveDocId, workOrderId: workOrderId! });
            if (response.success !== false) {
                toast.success(t('detail.attachmentDeleteSuccess'));
                await getWorkOrderDetail({ id: workOrderId!, showLoading: false });
            }
        } catch {
            toast.error(t('detail.attachmentDeleteError'));
        }
    }, [deleteWorkOrderAttachment, workOrderId, getWorkOrderDetail, t]);

    return (
        <Attachment
            attachments={attachments}
            maxFiles={ATTACHMENT_MAX_FILES}
            maxFileSize={ATTACHMENT_MAX_FILE_SIZE}
            allowedFileExtensions={allowedFileExtensions}
            canEdit={canEditAttachments}
            saving={attachmentSaving}
            onAdd={handleAddAttachments}
            onDelete={handleDeleteAttachment}
        />
    );
}

export default WorkOrderAttachment;